import { useState } from 'react';
import { styled } from '../../../../../stitches.config';
import { Button } from '../../../../controls/Button/Button';
import { type PricingEntry } from './Pricing.types';
import { Icon } from '../../../../text/Icon/Icon';

const PricingListContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$2',
  fontSize: '$2',
});

const Entry = styled('div', {
  'display': 'flex',
  'alignItems': 'center',
  'gap': '$6',
  'cursor': 'pointer',
  'padding': '$2 $3',
  'borderRadius': '$small',
  '&:hover': {
    backgroundColor: '$slate2',
  },
  'variants': {
    open: {
      true: {
        backgroundColor: '$slate2',
      },
    },
  },
});

const Price = styled('div', {
  width: '6rem',
  fontFamily: '$mono',
  textAlign: 'right',
});
const PriceUnit = styled('span', {
  color: '$slate9',
});

const Amount = styled('div', {
  width: '6rem',
  textAlign: 'right',
  variants: {
    mono: {
      true: {
        fontSize: '$2',
        fontFamily: '$mono',
      },
    },
  },
});
const AmountUnit = styled('span', {
  display: 'inline-block',
  width: '1rem',
  textAlign: 'left',
  color: '$slate9',
});

const Supplier = styled('div', {
  width: '15rem',
});

const Availability = styled('div', {
  width: '12rem',
});

const Header = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$6',
  color: '$slate9',
  fontSize: '$1',
  fontWeight: '700',
  textTransform: 'uppercase',
  borderBottom: '2px solid $slate6',
  marginBottom: '$2',
  paddingBottom: '$2',
});

const EntryDetails = styled('div', {
  display: 'flex',
  gap: '$6',
  flexWrap: 'wrap',
  backgroundColor: '$slate2',
  borderRadius: '$small',
  padding: '$3 $7',
  borderTop: '1px solid $slate6',
});

const EntryDetailsItem = styled('p', {
  margin: '$3 0',
  display: 'flex',
  alignItems: 'center',
  gap: '$2',
});

const EntryDetailsItemLabel = styled('span', {
  marginRight: '$3',
  color: '$slate9',
});

function buildAvailabilityTagProps(availabilityTier: number) {
  if (availabilityTier < 4) {
    return {
      theme: 'green',
      icon: 'check',
      text: 'In stock',
    };
  }

  if (availabilityTier === 4) {
    return {
      theme: 'orange',
      icon: 'science',
      text: 'Synthesis',
    };
  }

  if (availabilityTier === 5) {
    return {
      icon: 'circle',
      text: 'Custom',
    };
  }

  return {
    text: 'Unknown',
  };
}

interface PricingListProps {
  entries: PricingEntry[];
}

export function PricingList(props: PricingListProps) {
  const { entries } = props;

  const [openEntries, setOpenEntries] = useState<number[]>([]);

  const toggleEntry = (idx: number) => () => {
    setOpenEntries(prev => (prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]));
  };

  return (
    <PricingListContainer>
      <Header>
        <Price>SM Price</Price>
        <Amount>Amount</Amount>
        <Supplier>Supplier</Supplier>
        <Availability>Availability</Availability>
      </Header>
      {entries.map((entry, idx) => (
        <div key={idx}>
          <Entry onClick={toggleEntry(idx)} open={openEntries.includes(idx)}>
            <Price>
              <PriceUnit>$</PriceUnit>
              {entry.price}
            </Price>
            <Amount mono>
              {entry.amount[0]}
              <AmountUnit>{entry.amount[1]}</AmountUnit>
            </Amount>
            <Supplier>{entry.supplierName ?? 'n/a'}</Supplier>
            <Availability>
              <Button size="small" disabled {...(buildAvailabilityTagProps(entry.availabilityTier) as any)} />
            </Availability>
          </Entry>
          {openEntries.includes(idx) && (
            <EntryDetails>
              <EntryDetailsItem>
                <EntryDetailsItemLabel>Availability</EntryDetailsItemLabel>
                <Icon icon="schedule" />
                {entry.availabilityTierDescription}
              </EntryDetailsItem>
              <EntryDetailsItem>
                <EntryDetailsItemLabel>Compound name</EntryDetailsItemLabel>
                {entry.compoundName ?? 'n/a'}
              </EntryDetailsItem>
              <EntryDetailsItem>
                <EntryDetailsItemLabel>CAS number</EntryDetailsItemLabel>
                {entry.casNumber ?? 'n/a'}
              </EntryDetailsItem>
            </EntryDetails>
          )}
        </div>
      ))}
    </PricingListContainer>
  );
}
